import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { currentContextAtom } from '../../state';
import { RoutePaths } from '../../routes/AppRoutes';
import {
  canAccessWishlistAtom,
  wishlistItemsAtom,
  wishlistLoadingAtom,
  wishlistSupplierAtom,
} from '../state';
import {
  EMPTY_REMINDER_INTERVAL_DAYS,
  REMINDER_DISPLAY_DELAY_MS,
  WISHLIST_EMPTY_REMINDER_SHOWN,
  WISHLIST_LAST_ACTIVITY,
  WISHLIST_LAST_VISIT,
  daysSince,
  getStoredDate,
  isWishlistInactive,
  recordEmptyReminderShown,
} from '../wishlist.utils';


interface UseWishlistEmptyReminderResult {
  open: boolean;
  onClose: () => void;
  onCtaClick: () => void;
}

export default function useWishlistEmptyReminder(): UseWishlistEmptyReminderResult {
  const navigate = useNavigate();
  const { roleAssignmentId } = useAtomValue(currentContextAtom);
  const canAccessWishlist = useAtomValue(canAccessWishlistAtom);
  const wishlistItems = useAtomValue(wishlistItemsAtom);
  const wishlistSuppliers = useAtomValue(wishlistSupplierAtom);
  const wishlistLoading = useAtomValue(wishlistLoadingAtom);
  const [open, setOpen] = useState(false);

  const eligible = useMemo(() => {
    if (!canAccessWishlist || wishlistLoading || wishlistItems.length + wishlistSuppliers.length > 0) {
      return false;
    }

    const lastVisit = getStoredDate(WISHLIST_LAST_VISIT, roleAssignmentId);
    const lastActivity = getStoredDate(WISHLIST_LAST_ACTIVITY, roleAssignmentId);
    const lastEmptyReminderShown = getStoredDate(WISHLIST_EMPTY_REMINDER_SHOWN, roleAssignmentId);

    if (!isWishlistInactive(lastVisit, lastActivity, EMPTY_REMINDER_INTERVAL_DAYS)) {
      return false;
    }

    return !lastEmptyReminderShown || daysSince(lastEmptyReminderShown) >= EMPTY_REMINDER_INTERVAL_DAYS;
  }, [
    roleAssignmentId,
    canAccessWishlist,
    wishlistLoading,
    wishlistItems,
    wishlistSuppliers,
  ]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (eligible) {
      timer = setTimeout(() => {
        setOpen(true);
      }, REMINDER_DISPLAY_DELAY_MS);
    } else {
      setOpen(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [eligible]);

  const dismiss = (): void => {
    recordEmptyReminderShown(roleAssignmentId);
    setOpen(false);
  };

  const onClose = (): void => {
    dismiss();
  };

  const onCtaClick = (): void => {
    dismiss();
    navigate(generatePath(RoutePaths.DISCOVERY_SUPPLIERS));
  };

  return { open, onClose, onCtaClick };
}